"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import PinGate from "../components/PinGate";

// ── Types ────────────────────────────────────────────────────────────────────

type SymbolSummary = {
  symbol: string;
  isLong: boolean;
  heldQty: number;
  entryPrice: number | null;
  currentPrice: number | null;
  pnl: number | null;
  pnlPct: number | null;
  lastUpdated: string | null;
  signal: number | null;
  kalmanLevel: number | null;
  realisedVol: number | null;
  regime: "trending" | "choppy" | null;
  highWaterMark: number | null;
};

type JournalEntry = {
  timestamp: string;
  symbol: string;
  latest_close: number;
  kalman_level?: number;
  realised_vol?: number;
  regime?: "trending" | "choppy";
  signal: number;
  held_qty: number;
  action: string;
  order_id: string | null;
  next_day_return: number | null;
};

type SweepRow = {
  entry_buffer: number;
  exit_buffer: number;
  total_return_pct: number;
  ann_return_pct: number;
  sharpe: number;
  max_drawdown_pct: number;
  n_trades: number;
  time_in_market_pct: number;
  is_live: boolean;
};

type SweepResult = {
  symbol: string;
  period_start: string;
  period_end: string;
  n_days: number;
  bh_return_pct: number;
  rows: SweepRow[];
};

type AccountData = {
  equity: number;
  cash: number;
  portfolioVal: number;
  buyingPower: number;
  startingEquity: number;
  totalPnl: number;
  totalPnlPct: number;
  status: string;
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt$(n: number, decimals = 2) {
  return n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
function fmtPct(n: number, decimals = 2) {
  return `${n >= 0 ? "+" : ""}${n.toFixed(decimals)}%`;
}

// ── Regime badge ─────────────────────────────────────────────────────────────

function RegimeBadge({ regime }: { regime: "trending" | "choppy" | null }) {
  if (!regime) return <span className="text-slate-500 text-xs">—</span>;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
      regime === "trending"
        ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
        : "bg-amber-500/10 text-amber-400 border-amber-500/20"
    }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${regime === "trending" ? "bg-emerald-400" : "bg-amber-400"}`} />
      {regime}
    </span>
  );
}

// ── Paper account card ────────────────────────────────────────────────────────

function AccountCard() {
  const [acct, setAcct]       = useState<AccountData | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/trading/api/account")
      .then((r) => r.json())
      .then((d) => { if (d.error) setError(d.error); else setAcct(d); })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 animate-pulse h-24" />;

  if (error) return (
    <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4 text-xs text-amber-400">
      Account unavailable: {error}
    </div>
  );

  if (!acct) return null;
  const pnlPos = acct.totalPnl >= 0;

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">Paper Account</h3>
        <span className="rounded-full bg-sky-500/10 border border-sky-500/20 px-2.5 py-0.5 text-[10px] font-semibold text-sky-400 uppercase tracking-wide">
          Alpaca Paper
        </span>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div>
          <p className="text-xs text-slate-400">Total Equity</p>
          <p className="mt-1 font-mono text-xl font-bold text-slate-100">${fmt$(acct.equity)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-400">Total P&amp;L</p>
          <p className={`mt-1 font-mono text-xl font-bold ${pnlPos ? "text-emerald-400" : "text-red-400"}`}>
            {pnlPos ? "+" : "−"}${fmt$(Math.abs(acct.totalPnl))}
          </p>
          <p className={`text-[10px] ${pnlPos ? "text-emerald-400" : "text-red-400"}`}>
            {fmtPct(acct.totalPnlPct)} vs $100k start
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-400">Portfolio Value</p>
          <p className="mt-1 font-mono text-sm font-semibold text-slate-200">${fmt$(acct.portfolioVal)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-400">Cash</p>
          <p className="mt-1 font-mono text-sm font-semibold text-slate-200">${fmt$(acct.cash)}</p>
        </div>
      </div>
    </div>
  );
}

// ── Portfolio table ───────────────────────────────────────────────────────────

const SYMBOL_LABELS: Record<string, string> = {
  ITA: "U.S. Aerospace & Defence",
  QQQ: "Nasdaq 100",
  XLV: "Healthcare",
  SPY: "S&P 500",
  XLI: "Industrials",
};

function PortfolioTable({ bySymbol, symbols, loading }: {
  bySymbol: Record<string, SymbolSummary | null>;
  symbols: string[];
  loading: boolean;
}) {
  if (loading) return <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 animate-pulse h-48" />;

  // Show all configured symbols, falling back to an empty row if not yet in journal
  const allSymbols = symbols.length ? symbols : ["ITA", "QQQ", "XLV", "SPY", "GLD"];

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
      <div className="border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Portfolio</h3>
        <span className="text-xs text-slate-500">{allSymbols.length} symbols · equal-weight allocation</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-800 text-slate-400">
              <th className="px-4 py-2 text-left font-medium">Symbol</th>
              <th className="px-4 py-2 text-right font-medium">Price</th>
              <th className="px-4 py-2 text-right font-medium">Kalman Level</th>
              <th className="px-4 py-2 text-right font-medium">Signal</th>
              <th className="px-4 py-2 text-center font-medium">Regime</th>
              <th className="px-4 py-2 text-right font-medium">Vol (20d)</th>
              <th className="px-4 py-2 text-right font-medium">Position</th>
              <th className="px-4 py-2 text-right font-medium">P&amp;L</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {allSymbols.map((sym) => {
              const s = bySymbol[sym] ?? null;
              const entryTrigger = s?.kalmanLevel ? s.kalmanLevel * 1.03 : null;
              const dist = entryTrigger && s?.currentPrice
                ? ((s.currentPrice / entryTrigger) - 1) * 100
                : null;
              return (
                <tr key={sym} className={s?.isLong ? "bg-emerald-500/5" : ""}>
                  <td className="px-4 py-3">
                    <div className="font-mono font-semibold text-slate-100">{sym}</div>
                    <div className="text-[10px] text-slate-500">{SYMBOL_LABELS[sym] ?? ""}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-200">
                    {s?.currentPrice ? `$${s.currentPrice.toFixed(2)}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="font-mono text-slate-200">
                      {s?.kalmanLevel ? `$${s.kalmanLevel.toFixed(2)}` : "—"}
                    </div>
                    {dist !== null && (
                      <div className={`text-[10px] ${dist >= 0 ? "text-emerald-400" : "text-slate-500"}`}>
                        {dist >= 0 ? "+" : ""}{dist.toFixed(1)}% to entry
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {s !== null ? (
                      <span className={`rounded-full px-2 py-0.5 font-mono font-semibold ${
                        (s.signal ?? 0) > 0
                          ? "bg-emerald-500/15 text-emerald-400"
                          : "bg-slate-800 text-slate-500"
                      }`}>
                        {(s.signal ?? 0) > 0 ? `${Math.round((s.signal ?? 0) * 100)}%` : "flat"}
                      </span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <RegimeBadge regime={s?.regime ?? null} />
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {s?.realisedVol !== null && s?.realisedVol !== undefined ? (
                      <span className={s.realisedVol > 0.20 ? "text-amber-400" : "text-slate-300"}>
                        {(s.realisedVol * 100).toFixed(1)}%
                      </span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {s?.isLong ? (
                      <span className="text-emerald-400 font-semibold">{s.heldQty} shares</span>
                    ) : (
                      <span className="text-slate-500">flat</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {s?.isLong && s?.pnl !== null ? (
                      <div>
                        <div className={s.pnl >= 0 ? "text-emerald-400" : "text-red-400"}>
                          {s.pnl >= 0 ? "+" : "−"}${fmt$(Math.abs(s.pnl))}
                        </div>
                        <div className={`text-[10px] ${s.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {fmtPct(s.pnlPct ?? 0, 2)}
                        </div>
                      </div>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Trade log ─────────────────────────────────────────────────────────────────

function TradeLog({ entries }: { entries: JournalEntry[] }) {
  const [filter, setFilter] = useState<string>("ALL");
  const symbols = ["ALL", ...new Set(entries.map((e) => e.symbol))];
  const filtered = filter === "ALL" ? entries : entries.filter((e) => e.symbol === filter);

  if (!entries.length) return null;

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
      <div className="border-b border-slate-800 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-semibold">Trade Log</h3>
        <div className="flex gap-1">
          {symbols.map((sym) => (
            <button
              key={sym}
              onClick={() => setFilter(sym)}
              className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                filter === sym
                  ? "bg-sky-500/20 text-sky-400 border border-sky-500/30"
                  : "text-slate-500 hover:text-slate-300"
              }`}
            >
              {sym}
            </button>
          ))}
        </div>
      </div>
      <div className="divide-y divide-slate-800/60">
        {filtered.map((e, i) => (
          <div key={i} className="grid grid-cols-6 items-center gap-2 px-4 py-2 text-xs">
            <span className="text-slate-500 font-mono">
              {new Date(e.timestamp).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })}
            </span>
            <span className="font-mono font-semibold text-slate-300">{e.symbol}</span>
            <span className="font-mono text-slate-300">${e.latest_close.toFixed(2)}</span>
            <span className="font-mono text-slate-500 text-[10px]">
              {e.kalman_level ? `lvl $${e.kalman_level.toFixed(2)}` : ""}
            </span>
            <span className={`font-semibold ${
              e.action.startsWith("BUY")  ? "text-emerald-400"
              : e.action.startsWith("SELL") ? "text-red-400"
              : "text-slate-500"
            }`}>
              {e.action}
            </span>
            <span className={`rounded-full text-center px-2 py-0.5 font-mono ${
              (e.signal ?? 0) > 0 ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-800/60 text-slate-500"
            }`}>
              {(e.signal ?? 0) > 0 ? `${Math.round((e.signal ?? 0) * 100)}%` : "flat"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Sweep table ───────────────────────────────────────────────────────────────

function SweepTable({ sweep }: { sweep: SweepResult | null }) {
  if (!sweep || sweep.rows.length === 0) return null;
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
      <div className="border-b border-slate-800 px-4 py-3">
        <h3 className="text-sm font-semibold">Entry / Exit Buffer Sweep</h3>
        <p className="text-xs text-slate-400 mt-0.5">
          {sweep.symbol} · {sweep.period_start} → {sweep.period_end} · {sweep.n_days} days · B&amp;H {sweep.bh_return_pct >= 0 ? "+" : ""}{sweep.bh_return_pct.toFixed(2)}% · sorted by Sharpe
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-800 text-slate-400">
              <th className="px-4 py-2 text-left font-medium">Entry buf</th>
              <th className="px-4 py-2 text-left font-medium">Exit buf</th>
              <th className="px-4 py-2 text-right font-medium">Total Ret</th>
              <th className="px-4 py-2 text-right font-medium">Ann. Ret</th>
              <th className="px-4 py-2 text-right font-medium">Sharpe</th>
              <th className="px-4 py-2 text-right font-medium">Max DD</th>
              <th className="px-4 py-2 text-right font-medium">Trades</th>
              <th className="px-4 py-2 text-right font-medium">In Mkt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {sweep.rows.map((row, i) => {
              const isBest = i === 0;
              return (
                <tr key={`${row.entry_buffer}-${row.exit_buffer}`}
                  className={`${isBest ? "bg-emerald-500/5" : ""} ${row.is_live ? "ring-1 ring-inset ring-sky-500/30" : ""}`}>
                  <td className="px-4 py-2.5 font-mono">
                    <span className="text-slate-200">{(row.entry_buffer * 100).toFixed(1)}%</span>
                    {isBest && <span className="ml-2 text-emerald-400 text-[10px]">best</span>}
                    {row.is_live && !isBest && <span className="ml-2 text-sky-400 text-[10px]">live</span>}
                    {isBest && row.is_live && <span className="ml-1 text-sky-400 text-[10px]">· live</span>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-slate-200">{(row.exit_buffer * 100).toFixed(1)}%</td>
                  <td className={`px-4 py-2.5 text-right font-mono tabular-nums ${row.total_return_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {row.total_return_pct >= 0 ? "+" : ""}{row.total_return_pct.toFixed(2)}%
                  </td>
                  <td className={`px-4 py-2.5 text-right font-mono tabular-nums ${row.ann_return_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {row.ann_return_pct >= 0 ? "+" : ""}{row.ann_return_pct.toFixed(2)}%
                  </td>
                  <td className={`px-4 py-2.5 text-right font-mono tabular-nums ${row.sharpe >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {row.sharpe.toFixed(3)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-red-400">
                    {row.max_drawdown_pct.toFixed(2)}%
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-300">{row.n_trades}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-300">{row.time_in_market_pct.toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

function Dashboard() {
  const [bySymbol, setBySymbol]       = useState<Record<string, SymbolSummary | null>>({});
  const [symbols, setSymbols]         = useState<string[]>([]);
  const [entries, setEntries]         = useState<JournalEntry[]>([]);
  const [loading, setLoading]         = useState(true);
  const [sweep, setSweep]             = useState<SweepResult | null>(null);
  const [sweepRunning, setSweepRunning] = useState(false);

  const fetchJournal = useCallback(async () => {
    try {
      const res  = await fetch("/trading/api/journal");
      const data = await res.json();
      setBySymbol(data.bySymbol ?? {});
      setSymbols(data.symbols ?? []);
      setEntries(data.entries ?? []);
    } catch {
      // not available yet
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSweep = useCallback(async () => {
    fetch("/trading/api/sweep")
      .then((r) => r.json())
      .then((d) => d.rows?.length ? setSweep(d) : null)
      .catch(() => null);
  }, []);

  const runSweep = useCallback(async () => {
    setSweepRunning(true);
    try {
      await fetch("/trading/api/sweep/run", { method: "POST" });
      await fetchSweep();
    } finally {
      setSweepRunning(false);
    }
  }, [fetchSweep]);

  useEffect(() => {
    fetchJournal();
    fetchSweep();
  }, [fetchJournal, fetchSweep]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-950/80 backdrop-blur">
        <nav className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Link href="/" className="text-lg font-semibold tracking-tight">
            <span className="text-sky-400">Oli</span>Sense
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-400">Algorithmic Trading</span>
            <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs font-medium text-emerald-400">
              Step 2 of 3
            </span>
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-12 space-y-12">

        <section>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-400">Quantitative Finance</p>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Kalman Filter Trading Strategy
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-slate-300 sm:text-base">
            A two-state adaptive Kalman filter runs independently on each ETF. Long when price breaks
            3% above the filter&apos;s fair-value level; exits 2% below. Equal-weight capital allocation
            across 5 symbols; position halved automatically in choppy vol regimes.
          </p>
        </section>

        {/* Paper account */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Paper Account</h2>
          <AccountCard />
        </section>

        {/* Portfolio */}
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold">Portfolio</h2>
            <button
              onClick={fetchJournal}
              className="rounded-lg border border-slate-700 px-3 py-1 text-xs text-slate-400 hover:border-sky-500 hover:text-sky-400 transition-colors"
            >
              Refresh
            </button>
          </div>
          <PortfolioTable bySymbol={bySymbol} symbols={symbols} loading={loading} />
        </section>

        {/* Trade log */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Trade Log</h2>
          {loading ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 animate-pulse h-32" />
          ) : (
            <TradeLog entries={entries} />
          )}
        </section>

        {/* Parameter sweep */}
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold">Parameter Sweep</h2>
            <button
              onClick={runSweep}
              disabled={sweepRunning}
              className="rounded-lg border border-slate-700 px-3 py-1 text-xs text-slate-400 hover:border-sky-500 hover:text-sky-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sweepRunning ? "Running…" : "Re-run sweep"}
            </button>
          </div>
          {sweep ? (
            <SweepTable sweep={sweep} />
          ) : (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 text-sm text-slate-400">
              No sweep results yet — click Re-run sweep to generate.
            </div>
          )}
        </section>

        {/* Strategy mechanics */}
        <section className="grid gap-6 sm:grid-cols-2">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 space-y-3">
            <h3 className="font-semibold">Filter Design</h3>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex gap-2"><span className="text-sky-400">→</span> Hidden state: [level, slope]</li>
              <li className="flex gap-2"><span className="text-sky-400">→</span> Adaptive observation noise: scales with 10-day realised variance</li>
              <li className="flex gap-2"><span className="text-sky-400">→</span> Independent filter per ETF, shared parameters</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 space-y-3">
            <h3 className="font-semibold">Signal &amp; Execution</h3>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex gap-2"><span className="text-sky-400">→</span> Entry: price &gt; Kalman level × 1.03</li>
              <li className="flex gap-2"><span className="text-sky-400">→</span> Exit: price &lt; Kalman level × 0.98</li>
              <li className="flex gap-2"><span className="text-sky-400">→</span> Trailing stop: 7% from high-water mark</li>
              <li className="flex gap-2"><span className="text-sky-400">→</span> Choppy vol (&gt;20%): halve position size</li>
              <li className="flex gap-2"><span className="text-sky-400">→</span> 95% equity split equally across 5 symbols</li>
            </ul>
          </div>
        </section>

      </main>

      <footer className="border-t border-slate-800 py-6 mt-12">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} OliSense.</span>
          <span>Private · Algorithmic Trading Dashboard</span>
        </div>
      </footer>
    </div>
  );
}

// ── Entry ────────────────────────────────────────────────────────────────────

export default function TradingPage() {
  return (
    <PinGate title="Algorithmic Trading">
      <Dashboard />
    </PinGate>
  );
}
